import { useCallback, useEffect, useId, useRef, useState } from "react";
import { authMessages } from "@/features/auth/messages";
import { useAdminLogout } from "@/features/auth/hooks/useAdminLogout";
import { useAdminSessionEmail } from "@/hooks/useAdminSessionEmail";
import styles from "./AdminUserMenu.module.css";

type UserDisplay = {
  initials: string;
  name: string;
  role: string;
};

type AdminUserMenuProps = {
  user: UserDisplay;
};

export function AdminUserMenu({ user }: AdminUserMenuProps) {
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const sessionEmail = useAdminSessionEmail();
  const logout = useAdminLogout();

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    closeMenu();
    logout();
  }, [closeMenu, logout]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (!rootRef.current?.contains(target)) {
        closeMenu();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closeMenu, open]);

  return (
    <div className={styles.root} ref={rootRef} data-testid="admin-user-menu">
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        data-testid="admin-user-profile"
      >
        <span className={styles.avatar} aria-hidden>
          {user.initials}
        </span>
        <span className={styles.userText}>
          <span className={styles.userName}>{user.name}</span>
          <span className={styles.userRole}>{user.role}</span>
        </span>
        <span className={styles.chevron} aria-hidden>
          {open ? "▴" : "▾"}
        </span>
      </button>

      {open ? (
        <div
          id={menuId}
          className={styles.menu}
          role="menu"
          aria-label={authMessages.userMenu.accountMenuAriaLabel}
          data-testid="admin-user-menu-panel"
        >
          <div className={styles.menuHeader} role="presentation">
            <p className={styles.menuName}>{user.name}</p>
            <p className={styles.menuEmail} data-testid="admin-user-menu-email">
              {sessionEmail || "—"}
            </p>
          </div>
          <button
            type="button"
            className={styles.menuItem}
            role="menuitem"
            onClick={handleLogout}
            data-testid="admin-user-menu-logout"
          >
            {authMessages.userMenu.logout}
          </button>
        </div>
      ) : null}
    </div>
  );
}
